/**
 * useCardStream — subscribes to /ws/cards/:cardId/io and assembles a
 * single conversation history.
 *
 * State shape:
 *   - messages: append-only ordered list (system + user + agent)
 *   - streamingText: the in-flight assistant message; flushed into
 *     `messages` on `done` / `error`
 *   - sessionState: latest session lifecycle state observed via the
 *     status frame (used by SessionStatusBadge in the same panel)
 *
 * The hook exposes:
 *   - sendUserMessage(text): adds a local user message immediately;
 *     dispatch flows through the agentApi.dispatch endpoint outside
 *     this hook
 *
 * Reconnect strategy mirrors `useBoardEvents`: exponential backoff,
 * 1s → 30s cap, closes cleanly on unmount.
 */
import type { CardStreamFrame } from '@chaos/types/agent';
import type { SessionState } from '@chaos/types/session';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { env } from '../../env';

export type ChatRole = 'user' | 'assistant' | 'system' | 'tool';

export interface ChatMessage {
  id: string;
  role: ChatRole;
  /** Display content. Markdown for assistant; plain for user/system. */
  text: string;
  /** ISO timestamp when the client observed the message. */
  at: string;
  /** Token usage from the `done` frame, when available. */
  tokens?: { input?: number; output?: number; costUsd?: number };
}

export interface UseCardStreamReturn {
  messages: ChatMessage[];
  streamingText: string;
  sessionState: SessionState | null;
  connected: boolean;
  pushUserMessage: (text: string) => string;
  /** Inject a system message — used for "Session resumed" notices etc. */
  pushSystemMessage: (text: string) => void;
}

interface UseCardStreamOptions {
  enabled?: boolean;
  /** Override the WS URL — used by unit tests with mock servers. */
  url?: string;
  /** Custom WebSocket constructor — injected in tests. */
  WebSocketCtor?: typeof WebSocket;
}

function wsUrl(cardId: string): string {
  const base = env.VITE_API_BASE_URL;
  let url: URL;
  try {
    url = new URL(base, window.location.origin);
  } catch {
    url = new URL(window.location.origin);
  }
  const proto = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${url.host}/ws/cards/${cardId}/io`;
}

function newId(): string {
  // crypto.randomUUID is available in every browser we support and node 22.
  return crypto.randomUUID();
}

export function useCardStream(
  cardId: string | undefined,
  opts: UseCardStreamOptions = {},
): UseCardStreamReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [streamingText, setStreamingText] = useState('');
  const [sessionState, setSessionState] = useState<SessionState | null>(null);
  const [connected, setConnected] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closedByUs = useRef(false);
  const attemptRef = useRef(0);
  // Streaming buffer lives in a ref so token frames don't trigger a
  // double render — React batches setState calls anyway, but the ref
  // keeps the latest text available to the `done` frame handler.
  const bufferRef = useRef('');

  const enabled = opts.enabled !== false && Boolean(cardId);
  const overrideUrl = opts.url;
  const Ctor = opts.WebSocketCtor;

  useEffect(() => {
    if (!cardId || !enabled) return;

    const flushAssistant = (tokens?: ChatMessage['tokens']): void => {
      const finalText = bufferRef.current;
      if (finalText.length === 0) return;
      bufferRef.current = '';
      setStreamingText('');
      setMessages((prev) => [
        ...prev,
        {
          id: newId(),
          role: 'assistant',
          text: finalText,
          at: new Date().toISOString(),
          ...(tokens ? { tokens } : {}),
        },
      ]);
    };

    const handleFrame = (raw: string): void => {
      let frame: CardStreamFrame;
      try {
        frame = JSON.parse(raw) as CardStreamFrame;
      } catch {
        return;
      }
      switch (frame.event) {
        case 'hello':
        case 'ping':
          return;
        case 'token': {
          bufferRef.current += frame.text;
          setStreamingText(bufferRef.current);
          return;
        }
        case 'system': {
          setMessages((prev) => [
            ...prev,
            {
              id: newId(),
              role: 'system',
              text: frame.text,
              at: new Date().toISOString(),
            },
          ]);
          return;
        }
        case 'tool_call': {
          setMessages((prev) => [
            ...prev,
            {
              id: newId(),
              role: 'tool',
              text: `Tool call: ${frame.name}`,
              at: new Date().toISOString(),
            },
          ]);
          return;
        }
        case 'tool_result':
          return;
        case 'status': {
          setSessionState(frame.state as SessionState);
          if (frame.note) {
            setMessages((prev) => [
              ...prev,
              {
                id: newId(),
                role: 'system',
                text: frame.note ?? '',
                at: new Date().toISOString(),
              },
            ]);
          }
          return;
        }
        case 'done': {
          flushAssistant(
            frame.tokensInput || frame.tokensOutput || frame.costUsd
              ? {
                  ...(frame.tokensInput !== undefined ? { input: frame.tokensInput } : {}),
                  ...(frame.tokensOutput !== undefined ? { output: frame.tokensOutput } : {}),
                  ...(frame.costUsd !== undefined ? { costUsd: frame.costUsd } : {}),
                }
              : undefined,
          );
          return;
        }
        case 'error': {
          flushAssistant();
          setMessages((prev) => [
            ...prev,
            {
              id: newId(),
              role: 'system',
              text: `Error: ${frame.message}`,
              at: new Date().toISOString(),
            },
          ]);
          return;
        }
        default: {
          // Exhaustive switch — keep the compile-time check.
          const _exhaustive: never = frame;
          void _exhaustive;
        }
      }
    };

    const connect = (): void => {
      const Constructor = Ctor ?? WebSocket;
      const url = overrideUrl ?? wsUrl(cardId);
      const ws = new Constructor(url);
      wsRef.current = ws;

      ws.onopen = () => {
        attemptRef.current = 0;
        setConnected(true);
      };
      ws.onmessage = (msgEvent) => {
        const data =
          typeof msgEvent.data === 'string'
            ? msgEvent.data
            : new TextDecoder().decode(msgEvent.data as ArrayBuffer);
        handleFrame(data);
      };
      ws.onclose = () => {
        wsRef.current = null;
        setConnected(false);
        if (closedByUs.current) return;
        const delay = Math.min(30_000, 1_000 * 2 ** attemptRef.current);
        attemptRef.current += 1;
        reconnectTimer.current = setTimeout(connect, delay);
      };
      ws.onerror = () => {
        // No-op; onclose follows.
      };
    };

    closedByUs.current = false;
    connect();

    return () => {
      closedByUs.current = true;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
      try {
        wsRef.current?.close();
      } catch {
        // Already closed.
      }
      wsRef.current = null;
    };
  }, [cardId, enabled, overrideUrl, Ctor]);

  const pushUserMessage = useCallback((text: string): string => {
    const id = newId();
    setMessages((prev) => [...prev, { id, role: 'user', text, at: new Date().toISOString() }]);
    return id;
  }, []);

  const pushSystemMessage = useCallback((text: string): void => {
    setMessages((prev) => [
      ...prev,
      { id: newId(), role: 'system', text, at: new Date().toISOString() },
    ]);
  }, []);

  return useMemo<UseCardStreamReturn>(
    () => ({
      messages,
      streamingText,
      sessionState,
      connected,
      pushUserMessage,
      pushSystemMessage,
    }),
    [messages, streamingText, sessionState, connected, pushUserMessage, pushSystemMessage],
  );
}
